import { useEffect, useState } from "react";
import { Volume2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { listVoices, speak, stopSpeaking } from "@/lib/speech";
import { useT } from "@/lib/i18n";

export const DEFAULT_AGENT = {
  name: "", avatar_url: "https://images.pexels.com/photos/11650978/pexels-photo-11650978.jpeg?auto=compress&cs=tinysrgb&w=400",
  personality: "", language: "it", system_prompt: "", greeting: "", voice: "", voice_speed: 1, voice_pitch: 1,
  creativity: 0.7, response_length: "medium", blocked_words: [],
};

const Field = ({ label, children, hint }) => (
  <div className="space-y-2">
    <Label className="eyebrow">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const SliderField = ({ label, value, min, max, step, onChange, testId, fmt = (v) => v }) => (
  <Field label={`${label} · ${fmt(value)}`}>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} data-testid={testId} />
  </Field>
);

const VoiceFields = ({ form, set }) => {
  const { t } = useT();
  const [voices, setVoices] = useState([]);
  useEffect(() => { listVoices().then(setVoices); }, []);
  const filtered = voices.filter((v) => v.lang.toLowerCase().startsWith(form.language));
  const preview = () => {
    stopSpeaking();
    speak(form.greeting || t("voice_sample"), { voice: form.voice, rate: form.voice_speed, pitch: form.voice_pitch, lang: form.language });
  };
  return (
    <div className="rounded-lg border border-white/10 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="font-display font-bold">{t("voice_config")}</p>
        <Button type="button" size="sm" variant="outline" onClick={preview} data-testid="voice-preview-btn"><Volume2 className="h-4 w-4 mr-1" />{t("preview")}</Button>
      </div>
      <Field label={t("voice")} hint={t("voice_hint")}>
        <Select value={form.voice || "auto"} onValueChange={(v) => set("voice", v === "auto" ? "" : v)}>
          <SelectTrigger data-testid="agent-voice-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">{t("voice_auto")}</SelectItem>
            {(filtered.length ? filtered : voices).map((v) => <SelectItem key={v.name} value={v.name}>{v.name} ({v.lang})</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <SliderField label={t("speed")} value={form.voice_speed} min={0.5} max={2} step={0.1} onChange={(v) => set("voice_speed", v)} testId="agent-speed-slider" fmt={(v) => `${v.toFixed(1)}x`} />
        <SliderField label={t("pitch")} value={form.voice_pitch} min={0.5} max={2} step={0.1} onChange={(v) => set("voice_pitch", v)} testId="agent-pitch-slider" fmt={(v) => v.toFixed(1)} />
      </div>
    </div>
  );
};

export default function AgentForm({ initial, onSubmit, saving }) {
  const { t } = useT();
  const [form, setForm] = useState({ ...DEFAULT_AGENT, ...initial });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="agent-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("name")}><Input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nova" data-testid="agent-name-input" /></Field>
        <Field label={t("language")}>
          <Select value={form.language} onValueChange={(v) => set("language", v)}>
            <SelectTrigger data-testid="agent-language-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="it">Italiano</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
          </Select>
        </Field>
      </div>
      <Field label={t("avatar_url")}><Input value={form.avatar_url} onChange={(e) => set("avatar_url", e.target.value)} data-testid="agent-avatar-input" /></Field>
      <Field label={t("personality")}><Textarea rows={2} value={form.personality} onChange={(e) => set("personality", e.target.value)} placeholder={t("personality_ph")} data-testid="agent-personality-input" /></Field>
      <Field label={t("system_prompt")} hint={t("system_prompt_hint")}><Textarea rows={3} value={form.system_prompt} onChange={(e) => set("system_prompt", e.target.value)} data-testid="agent-prompt-input" /></Field>
      <Field label={t("greeting")}><Input value={form.greeting} onChange={(e) => set("greeting", e.target.value)} placeholder={t("greeting_ph")} data-testid="agent-greeting-input" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <SliderField label={t("creativity")} value={form.creativity} min={0} max={1} step={0.05} onChange={(v) => set("creativity", v)} testId="agent-creativity-slider" fmt={(v) => `${Math.round(v * 100)}%`} />
        <Field label={t("response_length")}>
          <Select value={form.response_length} onValueChange={(v) => set("response_length", v)}>
            <SelectTrigger data-testid="agent-length-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["short", "medium", "long"].map((x) => <SelectItem key={x} value={x}>{t(`len_${x}`)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <VoiceFields form={form} set={set} />
      <Field label={t("blocked_words")} hint={t("blocked_words_hint")}>
        <Input value={form.blocked_words.join(", ")} onChange={(e) => set("blocked_words", e.target.value.split(",").map((w) => w.trim()).filter(Boolean))} data-testid="agent-blocked-input" />
      </Field>
      <Button type="submit" disabled={saving} className="w-full rounded-full bg-indigo-600 hover:bg-indigo-500" data-testid="agent-save-btn">{saving ? t("saving") : t("save_agent")}</Button>
    </form>
  );
}
